import moment from "moment-timezone";
import { socialAccountableAccounts, stateAccountableAccounts } from "./accountableAccountServices";
import { getDBFormattedEndDate, getDBFormattedStartDate } from "./dateServices";
import { getPurchaseTypes, getSalesTypes } from "./documentServices";
import { getStringAsFloat, checkInt } from "./mathServices";

function stringifyJSON(data) {
    let s = JSON.stringify(data);
    s = s.replace(/{"/g, "{");
    s = s.replace(/,"/g, ",");
    return s.replace(/":/g, ":");
}

export function formatOrderBy(pagination: any, defaultField: string) {
    const fieldName =
        pagination.sortBy && pagination.sortBy.length ? pagination.sortBy[0] : defaultField;
    if (fieldName) {
        let direction = "";
        if (fieldName !== "updatedAt") {
            direction =
                pagination.descending && pagination.descending.length && pagination.descending[0]
                    ? "_DESC"
                    : "_ASC";
        } else {
            direction =
                pagination.descending && pagination.descending.length && pagination.descending[0]
                    ? "_ASC"
                    : "_DESC";
        }
        return fieldName + direction;
    }
    return undefined;
}

export function getBTMinMaxQueryVariables(
    accountsId,
    selectedAccountingClosing,
    filter,
    companyId,
    onlyAccounting: boolean,
): any {
    const variables: any = {
        companyId,
        orderBy: formatOrderBy(filter.pagination, "dateDebit"),
        where: {
            account: accountsId.length ? undefined : { companyId },
            accountId_in: accountsId.length ? accountsId : undefined,
            // dateDebit_gte: selectedAccountingClosing?.id ? undefined : getDBFormattedStartDate(filter.date.begin),
            // dateDebit_lte: selectedAccountingClosing?.id ? undefined : getDBFormattedEndDate(filter.date.end),
            // accountingClosing: selectedAccountingClosing?.id ? { id: selectedAccountingClosing.id } : undefined,
            // status: filter.reconciliationStatus === 'INFORMATION_REQUEST' ? 'INFORMATION_REQUEST' : undefined,
        },
    };

    if (selectedAccountingClosing?.id) {
        variables.where.accountingClosing = { id: selectedAccountingClosing.id };
    } else {
        if (filter.date.begin && moment(filter.date.begin).isValid()) {
            const beginDate = getDBFormattedStartDate(filter.date.begin);
            if (beginDate) {
                variables.where.dateDebit_gte = beginDate;
            }
        }
        if (filter.date.end && moment(filter.date.end).isValid()) {
            const endDate = getDBFormattedEndDate(filter.date.end);
            if (endDate) {
                variables.where.dateDebit_lte = endDate;
            }
        }
    }

    const AND = [];

    if (filter.search) {
        const searchCondition = [];
        searchCondition.push({ wordingDisplay_contains: filter.search });
        searchCondition.push({ wordingOriginal_contains: filter.search });

        const qFloat = getStringAsFloat(filter.search);
        if (qFloat !== undefined) {
            searchCondition.push({ amount: qFloat });
            searchCondition.push({ amount: -qFloat });
        }
        AND.push({
            OR: searchCondition,
        });
    }
    if (filter.payBy !== "ALL") {
        variables.where.transactionType = filter.payBy;
    }
    if (filter.reconciliationStatus === "INFORMATION_REQUEST") {
        variables.where.status = "INFORMATION_REQUEST";
    }
    if (filter.reconciliationStatus === "DELETED") {
        variables.where.status = "DELETED";
    } else if (filter.reconciliationStatus === "SUPPLIER_DEFINED") {
        variables.where.accountingLines_some = {
            thirdPartyCompany: { id_not: "" },
            reconciliation: null,
            accountableAccount: { name_starts_with: "401" },
        };
    } else if (filter.reconciliationStatus === "CUSTOMER_DEFINED") {
        variables.where.accountingLines_some = {
            thirdPartyCompany: { id_not: "" },
            reconciliation: null,
            accountableAccount: { name_starts_with: "411" },
        };
    } else if (filter.reconciliationStatus === "SOCIAL") {
        variables.where.accountingLines_some = {
            reconciliation: null,
            accountableAccount: { name_in: socialAccountableAccounts },
        };
    } else if (filter.reconciliationStatus === "STATE") {
        variables.where.accountingLines_some = {
            reconciliation: null,
            accountableAccount: { name_in: stateAccountableAccounts },
        };
    } else if (filter.reconciliationStatus === "ZTILL_RECEIPT") {
        variables.where.accountingLines_some = { reconciliation: { type: "ZTILL_RECEIPT" } };
    } else if (filter.reconciliationStatus === "CURRENT_ACCOUNT") {
        variables.where.accountingLines_some = {
            accountableAccount: { name: "455000" },
        };
    } else if (filter.reconciliationStatus === "DEBIT_CREDIT_ACCOUNT") {
        variables.where.accountingLines_some = {
            accountableAccount: { name: "467000" },
        };
    } else if (filter.reconciliationStatus === "OPERATOR_ACCOUNT") {
        variables.where.accountingLines_some = {
            accountableAccount: { name: "108000" },
        };
    } else if (filter.reconciliationStatus === "WITHOUT_INVOICE_TO_REPLACE") {
        variables.where.accountingLines_some = {
            reconciliation: {
                toReplaceDocumentType: "WITHOUT_INVOICE",
                toReplaceAccountingLineSuggestions_some: { id_not: "" },
            },
        };
    } else if (
        filter.reconciliationStatus === "JUSTIFIED" ||
        filter.reconciliationStatus === "VALIDATED"
    ) {
        variables.where.notJustifiedAmountSigned = 0;
        // variables.everyAccountingLines = { reconciliation: { status: "VALIDATED" } }
    } else if (filter.reconciliationStatus === "NOT_JUSTIFIED") {
        AND.push({
            OR: [
                { justifiedAmountSigned: 0 },
                { accountingLines_some: { accountableAccount: { name: "471000" } } },
            ],
        });
    } else if (filter.reconciliationStatus === "PARTIALLY_JUSTIFIED") {
        AND.push({
            OR: [
                { notJustifiedAmountSigned_not: 0, justifiedAmountSigned_not: 0 },
                { accountingLines_some: { accountableAccount: { name: "471000" } } },
            ],
        });
    }
    //
    //
    //
    // if (filter.reconciliationStatus === "NOT_RECONCILIATED_CUSTOMER") {
    //     AND.push({
    //         OR: [
    //             { reconciliation: null }
    //         ]
    //     })
    // }
    else if (filter.reconciliationStatus === "LETTERING_PENDING_VALIDATION") {
        AND.push({
            OR: [
                {
                    reconciliation: {
                        status_in: ["PENDING_VALIDATION", "PENDING_VALIDATION_FROM_CLIENT"],
                    },
                },
                {
                    accountingLines_some: {
                        reconciliation: {
                            status_in: ["PENDING_VALIDATION", "PENDING_VALIDATION_FROM_CLIENT"],
                        },
                    },
                },
            ],
        });
    } else if (filter.reconciliationStatus === "ALLOCATION_PENDING_VALIDATION") {
        variables.where.status = "ANALYSED";
        variables.where.justifiedAmountSigned = 0;
    }
    //
    // add default status removing delete bankTransaction
    //
    if (
        !variables.where.status &&
        !variables.where.status_in &&
        !variables.where.status_not &&
        !variables.where.status_not_in
    ) {
        variables.where.status_not = "DELETED";
    }

    if (onlyAccounting) {
        if (variables.where.accountingLines_some) {
            variables.where.accountingLines_some.type_in = ["MANAGACCOU", "ACCOUNTING"];
        } else {
            variables.where.accountingLines_some = { type_in: ["MANAGACCOU", "ACCOUNTING"] };
        }
    }

    if (filter?.tags?.length) {
        const tagIds = filter.tags.filter((t) => !!t).map((x) => x.id);
        if (tagIds.length) {
            const orCondition = [];
            orCondition.push({
                tags_some: { id_in: tagIds },
            });
            orCondition.push({
                accountingLines_some: {
                    // accountableAccount: { tag: { id_in: tagIds } }
                    tags_some: { id_in: tagIds },
                    //     OR:[
                    //     {
                    //         accountableAccount: {tag: {id_in: tagIds }}
                    //     },
                    //     {
                    //         tag: {id_in: tagIds }
                    //     }
                    // ]
                },
            });
            AND.push({
                OR: orCondition,
            });
            // orConditions.push(orCondition)
            // variables.tags = { id_in: tagIds }
        }
    }
    //
    // AND
    //
    if (AND.length) {
        variables.where.AND = AND;
    }

    return variables;
}

export function getCommonBTQueryVariables(
    accountsId,
    selectedAccountingClosing,
    filter,
    companyId,
    onlyAccounting,
): any {
    const variables = getBTMinMaxQueryVariables(
        accountsId,
        selectedAccountingClosing,
        filter,
        companyId,
        onlyAccounting,
    );

    if (filter.amount.type === "REVENUE") {
        variables.where.amount_gte =
            filter.amount.minimum.length > 0 ? parseFloat(filter.amount.minimum) : 0;
        if (filter.amount.maximum.length > 0) {
            variables.where.amount_lte = parseFloat(filter.amount.maximum);
        }
        // variables.where.minAmount = filter.amount.minimum.length > 0 ? parseFloat(filter.amount.minimum) : 0;
        // variables.where.maxAmount = filter.amount.maximum.length > 0 ? parseFloat(filter.amount.maximum) : undefined;
    } else if (filter.amount.type === "EXPENSE") {
        variables.where.amount_lte =
            filter.amount.minimum.length > 0 ? -parseFloat(filter.amount.minimum) : 0;

        if (filter.amount.maximum.length > 0) {
            variables.where.amount_gte = -parseFloat(filter.amount.maximum);
        }
        // variables.where.minAmount = filter.amount.maximum.length > 0 ? -parseFloat(filter.amount.maximum) : undefined;
        // variables.where.maxAmount = filter.amount.minimum.length > 0 ? -parseFloat(filter.amount.minimum) : 0;
    } else {
        if (filter.amount.minimum.length > 0) {
            variables.where.amount_gte = parseFloat(filter.amount.minimum);
        }
        if (filter.amount.maximum.length > 0) {
            variables.where.amount_lte = parseFloat(filter.amount.maximum);
        }
        // variables.where.minAmount = filter.amount.minimum.length > 0 ? parseFloat(filter.amount.minimum) : undefined;
        // variables.where.maxAmount = filter.amount.maximum.length > 0 ? parseFloat(filter.amount.maximum) : undefined;
    }

    return variables;
}

function getMinMaxPriceDocumentQueryVariables(
    companyId,
    selectedAccountingClosing,
    filter,
    onlyAccounting: boolean,
): any {
    let status =
        filter.status &&
        filter.status !== "ALL" &&
        filter.status !== "PENDING_VALIDATION" &&
        filter.status !== "RECONCILIATION_MISSING" &&
        filter.status !== "ACCOUNTABLE_ACCOUNT_TO_DEFINE"
            ? filter.status
            : undefined;
    let status_not_in;
    if (status) {
        if (status === "ANALYSED") {
            status = ["ANALYSED", "ANALYSED_OK"];
        } else {
            status = [status];
        }
    } else {
        status_not_in = ["DELETED", "DENIED"];
    }
    const orConditions = [];
    let reconciliation;
    let accountableAccount;
    let accountableAccountAuto;
    let alConditions;
    // let orCondition
    if (filter.status === "PENDING_VALIDATION") {
        reconciliation = { id_not: "" };
    } else if (filter.status === "RECONCILIATION_MISSING") {
        reconciliation = null;
    } else if (filter.status === "ACCOUNTABLE_ACCOUNT_TO_DEFINE") {
        alConditions = { accountableAccount: { name_in: ["600000", "700000"] } };
        // const orCondition = []
        // orCondition.push({
        //     AND: {
        //         accountableAccount: null,
        //         accountableAccountAuto: { name_in: ["600000", "700000"] }
        //     }
        // })
        // orCondition.push({
        //     accountableAccount: { name_in: ["600000", "700000"] }
        // })
        // orCondition.push({
        //     netAmountAccountableAccountDefined_not: true
        // })
        // orConditions.push(orCondition)
        status = ["VALIDATED"];
    }

    if (onlyAccounting) {
        if (alConditions) {
            alConditions.type_in = ["MANAGACCOU", "ACCOUNTING"];
        } else {
            alConditions = { type_in: ["MANAGACCOU", "ACCOUNTING"] };
        }
    }

    if (filter?.tags?.length) {
        const tagIds = filter.tags.filter((t) => !!t).map((x) => x.id);
        if (tagIds.length) {
            const orCondition = [];
            orCondition.push({
                tags_some: { id_in: tagIds },
            });
            orCondition.push({
                accountingLines_some: {
                    // accountableAccount: { tag: { id_in: tagIds } }
                    tags_some: { id_in: tagIds },
                    //     OR:[
                    //     {
                    //         accountableAccount: {tag: {id_in: tagIds }}
                    //     },
                    //     {
                    //         tag: {id_in: tagIds }
                    //     }
                    // ]
                },
            });
            orConditions.push(orCondition);
            // variables.tags = { id_in: tagIds }
        }
    }

    // OR: [
    //     { name_contains: $q }
    //     { displayName_contains: $q }
    //     { invoiceNumber_contains: $q }
    //     { grossAmount: $qFloat }
    //     { thirdPartyCompany: $thirdPartyCompany}
    // ]

    let thirdPartyCompany;
    if (filter.thirdPartyCompanyName) {
        thirdPartyCompany = { name: filter.thirdPartyCompanyName };
    }

    if (filter.search && filter.search.length) {
        const orCondition = [];
        orCondition.push({ thirdPartyCompany: { name_contains: filter.search } });
        orCondition.push({ name_contains: filter.search });
        orCondition.push({ displayName_contains: filter.search });
        orCondition.push({ invoiceNumber_contains: filter.search });
        let qFloat = getStringAsFloat(filter.search);
        if (qFloat !== undefined) {
            qFloat = Math.abs(qFloat);
        }
        if (qFloat !== undefined) {
            orCondition.push({ grossAmount: qFloat });
        }
        orConditions.push(orCondition);
    }
    let beginDate, endDate;
    if (!selectedAccountingClosing?.id) {
        if (filter.date.begin && moment(filter.date.begin).isValid()) {
            beginDate = getDBFormattedStartDate(filter.date.begin);
        }
        if (filter.date.end && moment(filter.date.end).isValid()) {
            endDate = getDBFormattedEndDate(filter.date.end);
        }
    }

    let type = filter.type && filter.type !== "ALL" ? filter.type : undefined;
    let types;

    if (type) {
        if (type === "EXPENSE") {
            types = ["PURCHASE_INVOICE", "EXPENSE_REPORT_DOCUMENT"];
            type = undefined;
        } else if (type === "SALES") {
            types = ["SALES_INVOICE", "ZTILL_RECEIPT"];
            type = undefined;
        }
    } else if (!type && filter.type === "ALL" && filter.allTypes?.length) {
        types = filter.allTypes;
    }

    let andCondition;
    if (orConditions.length > 1) {
        andCondition = [];
        for (const orCondition of orConditions) {
            andCondition.push({
                OR: orCondition,
            });
        }
    }

    const variables = {
        companyId,
        accountingClosingId: selectedAccountingClosing?.id || undefined,
        beginDate: beginDate,
        endDate: endDate,
        type,
        types,
        reconciliation: reconciliation,
        accountableAccount: accountableAccount,
        accountableAccountAuto: accountableAccountAuto,
        thirdPartyCompany: thirdPartyCompany,
        status: status,
        status_not_in: status_not_in,
        declaredStatus: filter.declaredStatus,
        accountingLines: alConditions,
        orCondition: orConditions.length === 1 ? orConditions[0] : undefined,
        andCondition: andCondition,
    };

    return variables;
}

function getCommonDocumentQueryVariables(
    companyId,
    selectedAccountingClosing,
    filter,
    onlyAccounting: boolean,
): any {
    const variables = getMinMaxPriceDocumentQueryVariables(
        companyId,
        selectedAccountingClosing,
        filter,
        onlyAccounting,
    );
    if (!filter.filterByPrice) {
        variables.minAmount = undefined;
        variables.maxAmount = undefined;
    } else if (getSalesTypes().indexOf(filter.type) !== -1) {
        variables.minAmount =
            filter.amount.minimum.length > 0 ? parseFloat(filter.amount.minimum) : undefined;
        variables.maxAmount =
            filter.amount.maximum.length > 0 ? parseFloat(filter.amount.maximum) : undefined;
    } else if (getPurchaseTypes().indexOf(filter.type) !== -1) {
        variables.minAmount =
            filter.amount.maximum.length > 0 ? -parseFloat(filter.amount.maximum) : undefined;
        variables.maxAmount =
            filter.amount.minimum.length > 0 ? -parseFloat(filter.amount.minimum) : undefined;
    } else if (filter.filterByPrice) {
        variables.minAmount = filter.amount.minimum ? parseFloat(filter.amount.minimum) : undefined;
        variables.maxAmount = filter.amount.maximum ? parseFloat(filter.amount.maximum) : undefined;
    }
    return variables;
}

export function getCommonDocumentWhere(
    companyId,
    selectedAccountingClosing,
    filter,
    onlyAccounting: boolean,
    minMax = false,
) {
    const variables = minMax
        ? getMinMaxPriceDocumentQueryVariables(
              companyId,
              selectedAccountingClosing,
              filter,
              onlyAccounting,
          )
        : getCommonDocumentQueryVariables(
              companyId,
              selectedAccountingClosing,
              filter,
              onlyAccounting,
          );
    const where = {
        companyId: companyId,
        thirdPartyCompany: variables.thirdPartyCompany,
        show: true,
        status_in: variables.status,
        status_not_in: variables.status_not_in,
        vatDeclarationId: variables.declaredStatus === "UNDECLARED" ? null : undefined,
        vatDeclarationId_not: variables.declaredStatus === "DECLARED" ? null : undefined,
        reconciliation: variables.reconciliation,
        accountableAccount: variables.accountableAccount,
        accountableAccountAuto: variables.accountableAccountAuto,
        type: variables.type,
        type_in: variables.types,
        type_not_in: variables.type_not_in,
        accountingClosing: variables.accountingClosing,
        date_gte: variables.beginDate,
        date_lte: variables.endDate,
        grossAmountSigned_gte: variables.minAmount,
        grossAmountSigned_lte: variables.maxAmount,
        accountingLines_some: variables.accountingLines,
        OR: variables.orCondition,
        AND: variables.andCondition,
    };
    return where;
}

export function getCommonInvoiceWhere(filter) {
    let where: any = {
        type: filter.type,
        names: [],
    };
    if (filter.thirdPartyCompanyName) {
        where.clientNames = [...filter.thirdPartyCompanyName];
    }
    if (filter.search) {
        where.search = filter.search;
    }
    if (filter.status !== "ALL") {
        where.status = filter.status;
    }
    return where;
}

export function getCommonExpenseReportWhere(filter) {
    let where: any = {};

    if (filter.search) {
        where.search = filter.search;
    }
    if (filter.status !== "ALL" && filter.status !== "") {
        where.status = filter.status;
    }
    if (filter.amount?.minimum) {
        where.min = filter.amount.minimum;
    }
    if (filter.amount?.maximum) {
        where.max = filter.amount.maximum;
    }
    if (filter.date?.begin) {
        where.begin = filter.date.begin;
    }
    if (filter.date?.end) {
        where.end = filter.date.end;
    }
    return where;
}

export function getCommonMailQueryVariables(companyId, filter): any {
    // debugger
    let thirdPartyCompany;
    if (filter.thirdPartyCompanyName) {
        thirdPartyCompany = { name: filter.thirdPartyCompanyName };
    }
    const variables: any = {
        companyId: companyId,
        search: filter.search.length > 0 ? filter.search : undefined,
        thirdPartyCompany: thirdPartyCompany,
        beginDate: getDBFormattedStartDate(filter.date.begin),
        endDate: getDBFormattedEndDate(filter.date.end),
        first: filter.pagination.rowsPerPage > 0 ? filter.pagination.rowsPerPage : 100,
        skip: filter.pagination.rowsPerPage * (filter.pagination.page - 1),
        orderBy: formatOrderBy(filter.pagination, "date"),
    };
    return variables;
}

export function getAccountingLineQuery(
    companyId,
    selectedAccountingClosing,
    filter,
    accountableAccountTable,
) {
    const where: any = {
        companyId,
        thirdPartyCompany: filter.thirdPartyCompanyName
            ? { name: filter.thirdPartyCompanyName }
            : undefined,
        OR: undefined,
        AND: [],
        date_gte: selectedAccountingClosing?.id
            ? undefined
            : getDBFormattedStartDate(filter.date.begin),
        date_lte: selectedAccountingClosing?.id
            ? undefined
            : getDBFormattedEndDate(filter.date.end),
        type_in: ["MANAGACCOU", "ACCOUNTING"],
        document: undefined,
        bankTransaction: undefined,
        amount_gte: filter.amount.minimum ? parseFloat(filter.amount.minimum) : undefined,
        amount_lte: filter.amount.maximum ? parseFloat(filter.amount.maximum) : undefined,
    };

    if (filter.type !== "ALL") {
        const values = filter.type.split("#");
        const type = values[0];
        if (type !== "BANK_TRANSACTION") {
            where.document = {
                companyId,
                type,
                status: "VALIDATED",
                accountingClosingId: selectedAccountingClosing?.id || undefined,
            };
        } else {
            where.bankTransaction = {
                accountId: values.length === 2 ? values[1] : undefined,
                status_not: "DELETED",
                accountingClosingId: selectedAccountingClosing?.id || undefined,
            };
        }
    } else {
        where.AND.push({
            OR: [
                {
                    document: {
                        status: "VALIDATED",
                        accountingClosingId: selectedAccountingClosing?.id || undefined,
                    },
                },
                {
                    bankTransaction: {
                        status_not: "DELETED",
                        accountingClosingId: selectedAccountingClosing?.id || undefined,
                    },
                },
            ],
        });
    }

    if (filter.search) {
        const values = filter.search.split(" ");
        for (const search of values) {
            if (!search) {
                continue;
            }
            const OR = [];
            let qFloat = getStringAsFloat(search);
            if (checkInt(search)) {
                if (
                    accountableAccountTable?.accountableAccounts?.find((aa) =>
                        aa.name?.startsWith(search),
                    )
                ) {
                    OR.push({ accountableAccount: { name_starts_with: search } });
                }
            } else if (!qFloat) {
                OR.push(
                    { wording_contains: search },
                    { thirdPartyCompany: { name_contains: search } },
                    { user: { firstname_contains: search } },
                    { user: { lastname_contains: search } },
                    { bankAccount: { name_contains: search } },
                    { accountableAccount: { description_contains: search } },
                );
            }
            if (!filter.type?.startsWith("BANK_TRANSACTION")) {
                OR.push({ document: { invoiceNumber_contains: search } });
            }
            if (qFloat !== undefined) {
                qFloat = Math.abs(qFloat);
                OR.push({ amount_gte: qFloat - 0.001, amount_lte: qFloat + 0.001 });
            }
            where.AND.push({
                OR,
            });
        }
    }

    debugger;

    if (where.AND.length === 1 && where.AND[0].OR) {
        where.OR = where.AND[0].OR;
        delete where.AND;
    }

    const query = {
        where,
        first: filter.pagination.rowsPerPage > 0 ? filter.pagination.rowsPerPage : 100,
        skip: filter.pagination.rowsPerPage * (filter.pagination.page - 1),
        orderBy: formatOrderBy(filter.pagination, "type"),
    };
    return query;
}

export function getGroupKeyQuery(filter) {
    const where = {
        OR: [{ siret_not: "" }, { siren_not: "" }, { parentCompany: { id_not: "" } }],
        AND: [],
    };

    if (filter.search) {
        const values = filter.search.split(" ");
        for (const search of values) {
            if (!search) {
                continue;
            }
            const OR = [];
            if (checkInt(search)) {
                OR.push({ siret_contains: search });
            } else {
                OR.push(
                    { name_contains: search },
                    { legalUnitName_contains: search },
                    { shortName_contains: search },
                    { tradeName_contains: search },
                );
            }
            where.AND.push({
                OR,
            });
        }
    }

    if (filter.groupKeySearch) {
        where.AND.push({ groupKey_contains: filter.groupKeySearch });
    }

    const query = {
        where,
        first: filter.rowsPerPage,
        skip: filter.rowsPerPage * (filter.page - 1),
    };
    return query;
}

export function getImportMapQuery(filter, company, specificQuery) {
    const where = {
        OR: [{ id_not: "" }],
        AND: [],
    };

    if (filter.search) {
        const values = filter.search.split(" ");
        for (const search of values) {
            if (!search) {
                continue;
            }
            const OR = [
                { sourceSiret_contains: search },
                { sourceIban_contains: search },
                { sourceName_contains: search },
                { sourceDescription_contains: search },
                { externalId_contains: search },
                { origin_contains: search },
                { application_contains: search },
            ];
            // if (checkInt(search)) {
            //     OR.push(
            //         { sourceSiret_contains: search },
            //         { sourceIban_contains: search },
            //         { sourceName_contains: search },
            //         { sourceDescription_contains: search },
            //     )
            // } else {
            //     OR.push(
            //         { sourceName_contains: search },
            //         { sourceDescription_contains: search },
            //     )
            // }
            where.AND.push({
                OR,
            });
        }
    }

    if (filter.typeSearch) {
        where.AND.push({ type: filter.typeSearch });
    }

    if (specificQuery) {
        where.AND.push({ origin: specificQuery.type });
    }

    where.AND.push({ companyId: company.id });

    const query = {
        where,
        first: filter.pagination.rowsPerPage,
        skip: filter.pagination.rowsPerPage * (filter.pagination.page - 1),
    };
    return query;
}

export function getCommonDocumentQueryFromALVariables(
    companyId,
    selectedAccountingClosing,
    filter,
): any {
    let qFloat = getStringAsFloat(filter.search);
    if (qFloat !== undefined) {
        qFloat = Math.abs(qFloat);
    }

    let reconciliation;
    if (filter.status === "PENDING_VALIDATION" || filter.status === "RECONCILIATION_DONE")
        reconciliation = { id_not: "" };
    else if (filter.status === "RECONCILIATION_MISSING") reconciliation = null;

    let thirdPartyCompany;
    if (filter.search && filter.search.length) {
        thirdPartyCompany = { name_contains: filter.search };
    }

    const variables: any = {
        companyId: companyId,
        q: filter.search.length > 0 ? filter.search : undefined,
        qFloat: qFloat,
        accountingClosingId: selectedAccountingClosing?.id || undefined,
        beginDate: selectedAccountingClosing?.id
            ? undefined
            : getDBFormattedStartDate(filter.date.begin),
        endDate: selectedAccountingClosing?.id ? undefined : getDBFormattedEndDate(filter.date.end),
        first: filter.pagination.rowsPerPage > 0 ? filter.pagination.rowsPerPage : 100,
        skip: filter.pagination.rowsPerPage * (filter.pagination.page - 1),
        orderBy: formatOrderBy(filter.pagination, "type"),
        // type: filter.type && filter.type !== 'ALL' ? filter.type : undefined,
        minAmount: undefined,
        maxAmount: undefined,
        useForReconciliation: true,
        reconciliation: reconciliation,
        thirdPartyCompany: thirdPartyCompany,
    };
    const type = filter.type && filter.type !== "ALL" ? filter.type : undefined;
    if (type) {
        variables.type = type;
    }
    if (getSalesTypes().indexOf(filter.type) !== -1) {
        variables.minAmount =
            filter.amount.minimum.length > 0 ? parseFloat(filter.amount.minimum) : undefined;
        variables.maxAmount =
            filter.amount.maximum.length > 0 ? parseFloat(filter.amount.maximum) : undefined;
    } else if (getPurchaseTypes().indexOf(filter.type) !== -1) {
        variables.minAmount =
            filter.amount.maximum.length > 0 ? -parseFloat(filter.amount.maximum) : undefined;
        variables.maxAmount =
            filter.amount.minimum.length > 0 ? -parseFloat(filter.amount.minimum) : undefined;
    }
    return variables;
}

function connectAccount(item) {
    if (item && item.accountableAccount) {
        return `accountableAccount:{connect:{id:"${item.accountableAccount.id}"}}`;
    }
    return "";
}

export function updateDocumentAccountableAccount(item: any) {
    if (item && item.accountableAccount) {
        return `updateDocument_${item.id} : updateDocument(
            where: {id:"${item.id}"},
            data:{
            ${connectAccount(item)}
            }
        ){id}\n`;
    }
    return "";
}
export function updateBTAccountableAccount(item: any) {
    if (item && item.accountableAccount) {
        return `updateBankTransaction_${item.id} : updateBankTransaction(
            where: {id:"${item.id}"},
            data:{
                accountableAccount:{connect:{id:"${item.accountableAccount.id}"}}
            }
        ){id}\n`;
    }
    return "";
}

export function updateDocumentFromReconciliation(item: any): string {
    if (!item) {
        return "";
    }
    const prefix = `updateDocument_${item.id} : updateDocument(
        where: {id:"${item.id}"},
        data:{`;

    let mutation = "";

    let subNetAmount1 = 0.0;
    if (item.netAmount_1) {
        subNetAmount1 = item.amountSigned < 0 ? item.netAmount_1 : -item.netAmount_1;
    }
    // if (item.credit_1) {
    //     if (expense) {
    //         subNetAmount1 = -parseFloat(item.credit_1);
    //     } else {
    //         subNetAmount1 = parseFloat(item.credit_1);
    //     }
    // } else if (item.debit_1) {
    //     if (expense) {
    //         subNetAmount1 = parseFloat(item.debit_1);
    //     } else {
    //         subNetAmount1 = -parseFloat(item.debit_1);
    //     }
    // }
    let subNetAmount2 = 0.0;
    if (item.netAmount_2) {
        subNetAmount2 = item.amountSigned < 0 ? item.netAmount_2 : -item.netAmount_2;
    }
    // if (item.credit_2) {
    //     if (expense) {
    //         subNetAmount2 = -parseFloat(item.credit_2);
    //     } else {
    //         subNetAmount2 = parseFloat(item.credit_2);
    //     }
    // } else if (item.debit_2) {
    //     if (expense) {
    //         subNetAmount2 = parseFloat(item.debit_2);
    //     } else {
    //         subNetAmount2 = -parseFloat(item.debit_2);
    //     }
    // }
    let addAccountableAccount = true;

    let data = item.doc.data;
    if (subNetAmount1 && subNetAmount2) {
        addAccountableAccount = false;
        if (!data) {
            data = {};
        }
        data.subNetAmounts = [];
        data.subNetAmounts.push({
            netAmountSigned: subNetAmount1,
            accountableAccountName: item.accountableAccount1.name,
        });
        data.subNetAmounts.push({
            netAmountSigned: subNetAmount2,
            accountableAccountName: item.accountableAccount2.name,
        });
    } else if (data && data.subNetAmounts) {
        delete data.subNetAmounts;
    }
    if (data) {
        mutation += ` data: ${stringifyJSON(data)}`;
    }
    // let spreadAccountableAccountId;
    if (addAccountableAccount && item.accountableAccount) {
        if (
            !item.doc.accountableAccount ||
            item.doc.accountableAccount.id !== item.accountableAccount.id
        ) {
            // mutation += `accountableAccount:{connect:{id:"${item.accountableAccount.id}"}}`;
            mutation += connectAccount(item);
            // spreadAccountableAccountId = item.accountableAccount.id;
        }
    } else if (item.doc.accountableAccount) {
        mutation += `accountableAccount: {disconnect:true}`;
    }

    if (item.thirdPartyAccountableAccount) {
        if (item.thirdPartyAccountableAccount.id) {
            if (
                !item.doc.thirdPartyAccountableAccount ||
                item.doc.thirdPartyAccountableAccount.id !== item.thirdPartyAccountableAccount.id
            ) {
                mutation += `thirdPartyAccountableAccount:{connect:{id:"${item.thirdPartyAccountableAccount.id}"}}`;
            }
        } else if (item.thirdPartyAccountableAccount.name) {
            if (
                !item.doc.thirdPartyAccountableAccount ||
                item.doc.thirdPartyAccountableAccount.name !==
                    item.thirdPartyAccountableAccount.name
            ) {
                mutation += `thirdPartyAccountableAccount:{connect:{name:"${item.thirdPartyAccountableAccount.name}"}}`;
            }
        }
    }

    if (!mutation) {
        return "";
    }
    mutation = prefix + mutation;
    mutation += `}){id}\n`;

    return mutation;
}

export function updateBTFromReconciliation(item: any): string {
    if (!item) {
        debugger;
        return "";
    }
    const prefix = `updateBankTransaction_${item.id} : updateBankTransaction(
        where: {id:"${item.id}"},
        data:{\n`;

    let mutation = "";
    // let amount1 = 0.0;
    // if (item.credit_1) {

    //     amount1 = -parseFloat(item.credit_1);
    // } else if (item.debit_1) {

    //     amount1 = parseFloat(item.debit_1);
    // }
    // let amount2 = 0.0;
    // if (item.credit_2) {

    //     amount2 = -parseFloat(item.credit_2);
    // } else if (item.debit_2) {

    //     amount2 = parseFloat(item.debit_2);
    // }
    // let addAccountableAccount = true;
    // if (amount1 && amount2) {
    //     addAccountableAccount = false;
    //     mutation += `amount1: ${amount1}
    //                  amount2: ${amount2}\n`;
    //     // accountableAccount1
    //     if (item.accountableAccount1) {
    //         if (!item.bt.accountableAccount1 || item.bt.accountableAccount1.id !== item.accountableAccount1.id) {
    //             mutation += `accountableAccount1:{connect:{id:"${item.accountableAccount1.id}"}}`;
    //         }
    //     } else if (item.bt.accountableAccount1) {
    //         mutation += `accountableAccount1:{disconnect:true}`;
    //     }
    //     // accountableAccount2
    //     if (item.accountableAccount2) {
    //         if (!item.bt.accountableAccount2 || item.bt.accountableAccount2.id !== item.accountableAccount2.id) {
    //             mutation += `accountableAccount2:{connect:{id:"${item.accountableAccount2.id}"}}`;
    //         }
    //     } else if (item.bt.accountableAccount2) {
    //         mutation += `accountableAccount2:{disconnect:true}`;
    //     }
    // } else {
    //     mutation += `amount1: null
    //                  amount2: null\n`;
    //     if (item.bt.accountableAccount1) {
    //         mutation += `accountableAccount1:{disconnect:true}`;
    //     }
    //     if (item.bt.accountableAccount2) {
    //         mutation += `accountableAccount1:{disconnect:true}`;
    //     }
    // }
    // if (addAccountableAccount && item.accountableAccount) {
    if (item.accountableAccount) {
        if (
            !item.bt.accountableAccount ||
            item.bt.accountableAccount.name !== item.accountableAccount.name
        ) {
            mutation += `accountableAccount:{connect:{name:"${item.accountableAccount.name}"}}`;
        }
    } else if (item.bt.accountableAccount) {
        mutation += `accountableAccount:{disconnect: true}`;
    }
    if (!mutation) {
        return "";
    }
    mutation = prefix + mutation;
    mutation += `}){id}\n`;

    return mutation;
}

// export function updateBTFromReconciliation1(item:any, expense)
// {
//     if (item && item.accountableAccount)
//     {
//         return `updateBankTransaction_${item.id} : updateBankTransaction(
//             where: {id:"${item.id}"},
//             data:{
//                 accountableAccount:{connect:{id:"${item.accountableAccount.id}"}}
//             }
//         ){id}`
//     }
//     return "";
// }
